import React, { useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Shield } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import { useAuth } from "../contexts";
import { Card } from "../components/ui/card";
import { ShareToWhatsApp } from "../components/common/ShareToWhatsApp";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const DigitalIDCardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef(null);

  const studentId = `YCPS-${(user?.id || "000000").slice(-6).toUpperCase()}`;
  const fullName = `${user?.first_name || "Student"} ${user?.last_name || "User"}`;
  const role = user?.user_type === "teacher" ? "Teaching Staff" : user?.user_type === "admin" ? "Administrator" : "Student";
  const grade = user?.grade_level || user?.class_level || "N/A";

  const qrData = `Name: ${fullName}\nID: ${studentId}\nRole: ${role}\nSchool: Young Christian Preparatory School / JHS\nTakoradi, Ghana`;

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement("a");
      link.download = `YCPS_ID_${fullName.replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
    }
  }, [fullName]);

  if (!user) {
    navigate("/portal");
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="digital-id-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`}
            data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <Shield size={28} />
            <div>
              <h1 className="text-2xl font-bold">Digital ID Card</h1>
              <p className="text-white/70 text-sm">Your official YCPS identification</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 flex flex-col items-center gap-6">
        {/* ID Card */}
        <div ref={cardRef} className="w-[420px] max-w-full">
          <Card className="overflow-hidden border-0 shadow-2xl rounded-2xl">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] px-6 py-4 flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_prep-dashboard-5/artifacts/rih2hxkp_WhatsApp%20Image%202026-01-20%20at%204.53.18%20AM.jpeg" alt="YCPS Crest" className="w-10 h-10 object-contain" />
              </div>
              <div className="text-white">
                <h2 className="font-bold text-sm leading-tight">Young Christian Preparatory School / JHS</h2>
                <p className="text-white/60 text-xs">Takoradi, Ghana &bull; Est. 1970</p>
              </div>
            </div>

            {/* Card Body */}
            <div className="bg-white px-6 py-5">
              <div className="flex gap-5">
                {/* Photo placeholder */}
                <div className="w-24 h-28 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl border-2 border-[#002147]/10 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-inner">
                  <div className="w-full h-full flex items-center justify-center bg-[#002147]/5">
                    <span className="text-3xl font-bold text-[#002147]/30">
                      {(user?.first_name?.[0] || "")}{(user?.last_name?.[0] || "")}
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-[#002147] truncate" data-testid="id-card-name">{fullName}</h3>
                  <div className="mt-2 space-y-1.5">
                    <InfoRow label="ID Number" value={studentId} testId="id-card-number" />
                    <InfoRow label="Role" value={role} testId="id-card-role" />
                    {user?.user_type === "student" && <InfoRow label="Class" value={grade} testId="id-card-grade" />}
                    <InfoRow label="Email" value={user?.email || "N/A"} testId="id-card-email" />
                  </div>
                </div>
              </div>

              {/* QR Section */}
              <div className="mt-4 pt-4 border-t border-dashed border-gray-200 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Scan to verify</p>
                  <p className="text-xs text-gray-400 mt-0.5">Academic Year 2025-2026</p>
                </div>
                <div className="p-2 bg-white rounded-lg border border-gray-100 shadow-sm" data-testid="id-card-qr">
                  <QRCodeSVG value={qrData} size={72} level="M" />
                </div>
              </div>
            </div>

            {/* Card Footer */}
            <div className="bg-gradient-to-r from-[#FF6B00] to-[#ff8533] px-6 py-2 text-center">
              <p className="text-white text-xs font-medium tracking-wide">Excellence in Education Since 1970</p>
            </div>
          </Card>
        </div>

        {/* Download Button */}
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={handleDownload}
            className={`inline-flex items-center gap-2 px-8 py-4 bg-[#002147] text-white font-bold rounded-2xl text-lg ${btn3D} cursor-pointer select-none`}
            data-testid="download-id-btn">
            <Download size={22} /> Download ID Card
          </button>
          <ShareToWhatsApp text={`Check out my YCPS Digital ID Card! I'm ${fullName}, ${role} at Young Christian Preparatory School, Takoradi.`} />
        </div>

        <p className="text-[#002147]/50 text-xs text-center max-w-sm">
          This digital ID card is for identification purposes within YCPS. 
          Present the QR code for quick verification at school events and activities.
        </p>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, testId }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-xs text-gray-400 w-16 flex-shrink-0">{label}</span>
    <span className="text-sm font-semibold text-[#002147] truncate" data-testid={testId}>{value}</span>
  </div>
);

export default DigitalIDCardPage;
